import { useMemo } from 'react';
import type { ReactNode } from 'react';
import toast from 'react-hot-toast';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../hooks/useEntitlements';
import logoImage from '../images/MicroFeedback_Text_Logo.png';

interface LayoutProps {
  mode?: 'authenticated' | 'public';
  children?: ReactNode;
}

export function Layout({ mode = 'authenticated', children }: LayoutProps) {
  const { user, signOut } = useAuth();
  const { entitlements } = useEntitlements();
  const location = useLocation();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Successfully signed out!');
    } catch (error) {
      console.error('Sign out failed:', error);
      toast.error('Failed to sign out. Please try again.');
    }
  };

  // Base navigation links for authenticated mode
  const baseNavigationLinks = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/dashboard/billing', label: 'Billing' },
    { path: '/dashboard/settings', label: 'Settings' },
  ];

  // Check if user has admin entitlement
  const hasAdminAccess = useMemo(() => entitlements.has('admin'), [entitlements]);

  // Add admin tab only for authorized users
  const navigationLinks = hasAdminAccess
    ? [
        ...baseNavigationLinks,
        { path: '/dashboard/admin', label: 'Admin' },
      ]
    : baseNavigationLinks;

  // Render public layout
  if (mode === 'public') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        {/* Navigation */}
        <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-slate-200 z-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img src={logoImage} alt="MicroFeedback" className="h-8 w-auto" />
                </Link>
              </div>
              <div className="flex items-center space-x-4">
                <a
                  href="/demo"
                  className="text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Demo
                </a>
                <Link
                  to="/login"
                  className="text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/login"
                  className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Page content */}
        <main className="pt-24">{children}</main>

        {/* Footer */}
        <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200 mt-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">MicroFeedback</h3>
                <p className="text-slate-600 text-sm">
                  Simple, powerful feedback collection for modern websites.
                </p>
              </div>
              <div>
                {/* <h4 className="font-semibold text-slate-900 mb-4">Product</h4> */}
                <ul className="space-y-2 text-sm text-slate-600">
                  {/* <li><a href="#" className="hover:text-slate-900">Features</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Pricing</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Documentation</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">API</a></li> */}
                </ul>
              </div>
              <div>
                {/* <h4 className="font-semibold text-slate-900 mb-4">Company</h4> */}
                <ul className="space-y-2 text-sm text-slate-600">
                  {/* <li><a href="#" className="hover:text-slate-900">About</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Blog</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Careers</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Contact</a></li> */}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Support</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  {/* <li><a href="#" className="hover:text-slate-900">Help Center</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Status</a></li> */}
                  <li><a href="/privacy" className="hover:text-slate-900">Privacy</a></li>
                  {/* <li><a href="#" className="hover:text-slate-900">Terms</a></li> */}
                </ul>
              </div>
            </div>
            <div className="border-t border-slate-200 mt-8 pt-8 text-center">
              <p className="text-sm text-slate-500"> 
                © {new Date().getFullYear()} MicroFeedback. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Render authenticated layout (default)
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <img 
                src={logoImage} 
                alt="MicroFeedback" 
                className="h-8 w-auto"
              />
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <div className="flex items-center space-x-3">
                  <img
                    className="h-8 w-8 rounded-full"
                    src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || 'User')}&background=6366f1&color=fff`}
                    alt={user.displayName || user.email || 'User'}
                  />
                  <span className="text-sm text-gray-700">
                    {user.displayName || user.email}
                  </span>
                </div>
              )}
              <button 
                onClick={handleSignOut}
                className="text-gray-500 hover:text-gray-700"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Secondary navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${
                  location.pathname === link.path
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
} 